import {
  deviationSchema,
  deviationExtendedSchema,
} from '../normalizr/schemas/deviation';
import set from 'lodash/set';
import {
  NormalizedDeviation,
  NormalizedExtendedDeviation,
} from '../normalizr/types';
import {
  PapiEditorText,
  PapiUser,
  PapiGallection,
  PapiExtendedGroup,
} from '@wix/da-papi-types';
import { userSchema } from '../normalizr/schemas/user';
import {
  galleryFolderSchema,
  collectionFolderSchema,
  gallectionSchema,
} from '../normalizr/schemas/folder';

// we want to preserve expandables if the incoming entity was not expanded
// otherwise, overwrite everything

function preserveExisting<T>(existing: T | undefined, incoming: T | undefined) {
  if (existing !== undefined && incoming === undefined) {
    return existing;
  }

  return incoming;
}

function mergeTextContent(
  existing?: PapiEditorText,
  incoming?: PapiEditorText
) {
  if (!existing || !incoming) {
    return incoming;
  }

  // if the existing is expanded, and the incoming is not, keep the existing
  // do not attempt to merge to avoid tearing between excerpt and markup
  if (
    existing.html.markup !== undefined &&
    incoming.html.markup === undefined
  ) {
    return existing;
  }

  return incoming;
}

function dropUndefined<T>(thing: T) {
  const copy: Partial<T> = {};
  Object.keys(thing as any).forEach(key => {
    if (thing[key] !== undefined) {
      copy[key] = thing[key];
    }
  });
  return copy as T;
}

function mergeDeviationEntity(
  existing: NormalizedDeviation,
  incoming: NormalizedDeviation
): NormalizedDeviation {
  const merged = {
    ...incoming,
    textContent: mergeTextContent(existing.textContent, incoming.textContent),
    dailyDeviation: preserveExisting(
      existing.dailyDeviation,
      incoming.dailyDeviation
    ),
    background: preserveExisting(existing.background, incoming.background),
    hyped: preserveExisting(existing.hyped, incoming.hyped),
    extended: preserveExisting(existing.extended, incoming.extended),
  };
  return dropUndefined(merged);
}

function mergeDeviationExtendedEntity(
  existing: NormalizedExtendedDeviation,
  incoming: NormalizedExtendedDeviation
): NormalizedExtendedDeviation {
  return {
    ...existing,
    ...incoming,
  };
}

// groups are stupid and we need to fully merge the extended data
function mergedExtendedGroup(
  existing?: PapiExtendedGroup,
  incoming?: PapiExtendedGroup
) {
  if (!existing || !incoming) {
    return incoming ?? existing;
  }

  const existingApps = existing.apps;
  const incomingApps = incoming.apps;

  if (!existingApps || !incomingApps) {
    return {
      ...existing,
      ...incoming,
      apps: incomingApps ?? existingApps,
    };
  }

  const merged: PapiExtendedGroup = {
    ...existing,
    ...incoming,
    apps: dropUndefined({
      ...incomingApps,
      gallery: incomingApps.gallery ?? existingApps.gallery,
      posts: incomingApps.posts ?? existingApps.posts,
      join: incomingApps.join ?? existingApps.join,
    }),
  };

  return dropUndefined(merged);
}

function mergeUserEntity(existing: PapiUser, incoming: PapiUser): PapiUser {
  // we need to preserve this one especially because it might be absent even
  // when all other things in the expand are present
  const latestPostTs = existing.extendedGroup?.apps?.posts?.latestPostTs;

  const merged = {
    ...incoming,
    details: preserveExisting(existing.details, incoming.details),
    geo: preserveExisting(existing.geo, incoming.geo),
    profile: preserveExisting(existing.profile, incoming.profile),
    stats: preserveExisting(existing.stats, incoming.stats),
    isWatching: preserveExisting(existing.isWatching, incoming.isWatching),
    isBirthweek: preserveExisting(existing.isBirthweek, incoming.isBirthweek),
    networkbar: preserveExisting(existing.networkbar, incoming.networkbar),
    extendedGroup: mergedExtendedGroup(
      existing.extendedGroup,
      incoming.extendedGroup
    ),
  };

  if (latestPostTs && !merged.extendedGroup?.apps?.posts?.latestPostTs) {
    set(merged, 'extendedGroup.apps.posts.latestPostTs', latestPostTs);
  }

  return dropUndefined(merged);
}

function mergeGallectionEntity(
  existing: PapiGallection,
  incoming: PapiGallection
): PapiGallection {
  const merged = {
    ...incoming,
    totalItemCount: preserveExisting(
      existing.totalItemCount,
      incoming.totalItemCount
    ),
    hasSubfolders: preserveExisting(
      existing.hasSubfolders,
      incoming.hasSubfolders
    ),
    subfolders: preserveExisting(existing.subfolders, incoming.subfolders),
  };
  return dropUndefined(merged);
}

export const entityMergers: {
  [type: string]: (a, b) => any;
} = {
  [deviationSchema.key]: mergeDeviationEntity,
  [deviationExtendedSchema.key]: mergeDeviationExtendedEntity,
  [userSchema.key]: mergeUserEntity,
  [galleryFolderSchema.key]: mergeGallectionEntity,
  [collectionFolderSchema.key]: mergeGallectionEntity,
  [gallectionSchema.key]: mergeGallectionEntity,
};
